import { useState } from "react";
import { MdFavoriteBorder, MdFavorite, MdMoreVert, MdDelete, MdEdit } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import usePermission from "hooks/usePermission";

const PremiumFolderCard = ({ folder, onFavoriteToggle, onDelete, onEdit, type = "Lead" }) => {
  const navigate = useNavigate();
  const { can } = usePermission();
  const canDeleteFolder = can("folder_management", "delete");
  const [showMenu, setShowMenu] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const gradientStyle = folder.gradient
    ? {
      background: `linear-gradient(${folder.gradient.angle}, ${folder.gradient.colors[0]}, ${folder.gradient.colors[1]})`,
    }
    : {
      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    };

  const handleFolderClick = () => {
    const routeType = type === "Client" ? "clients" : "leads";
    navigate(`/admin/folders/${routeType}/${folder._id}`, { state: { folder, type } });
  };

  const handleFavoriteClick = (e) => {
    e.stopPropagation();
    onFavoriteToggle(folder._id, folder.isFavorite);
  };

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    setShowDeleteModal(true);
    setShowMenu(false);
  };

  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    try {
      await onDelete(folder._id);
      setShowDeleteModal(false);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setIsDeleting(false);
  };

  const handleEditClick = (e) => {
    e.stopPropagation();
    if (onEdit) {
      onEdit(folder);
    }
    setShowMenu(false);
  };
  const itemCount = folder.itemCount || 0;

  return (
    <div
      className="group relative flex flex-col h-auto cursor-pointer transition-all duration-300 hover:shadow-lg rounded-lg overflow-hidden"
      onClick={handleFolderClick}
    >
      {/* Folder Container */}
      <div className="relative bg-white dark:bg-navy-800 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 overflow-hidden border border-gray-100 dark:border-navy-700">

        {/* Folder Header with Tab Effect */}
        <div
          className="relative pt-0 pb-0 transition-all duration-300"
          style={gradientStyle}
        >
          {/* Folder Tab - top-left corner */}
          <div className="flex items-start h-12 relative">
            {/* Tab Shape */}
            <div className="w-2/3 h-full rounded-b-lg flex items-center px-3 py-0 relative" style={gradientStyle}>
              {/* Action Buttons in Header */}
              <div className="absolute right-2 top-2 flex gap-1 z-10">
                <button
                  onClick={handleFavoriteClick}
                  className="p-1.5 hover:bg-white/20 rounded-md transition-all duration-200 text-sm"
                  title={folder.isFavorite ? "Remove from favorites" : "Add to favorites"}
                >
                  {folder.isFavorite ? (
                    <MdFavorite className="text-red-300 text-base" />
                  ) : (
                    <MdFavoriteBorder className="text-white/70 text-base hover:text-white" />
                  )}
                </button>

                <div className="relative">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowMenu(!showMenu);
                    }}
                    className="p-1.5 hover:bg-white/20 rounded-md transition-all duration-200 text-sm"
                    title="Folder options"
                  >
                    <MdMoreVert className="text-white/70 text-base hover:text-white" />
                  </button>

                  {showMenu && (
                    <div className="absolute top-full right-0 mt-1 bg-white dark:bg-navy-900 rounded-lg shadow-xl z-20 overflow-hidden min-w-max border border-gray-100 dark:border-navy-700">
                      <button
                        onClick={handleEditClick}
                        className="flex items-center gap-2 px-3 py-2 hover:bg-blue-50 dark:hover:bg-navy-700 text-blue-600 dark:text-blue-400 w-full text-left transition-colors duration-200 text-sm"
                      >
                        <MdEdit className="text-sm" />
                        Edit
                      </button>
                      {canDeleteFolder && (
                        <button
                          onClick={handleDeleteClick}
                          className="flex items-center gap-2 px-3 py-2 hover:bg-red-50 dark:hover:bg-red-900/20 text-red-600 dark:text-red-400 w-full text-left transition-colors duration-200 text-sm"
                        >
                          <MdDelete className="text-sm" />
                          Delete
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Light shine effect on hover */}
          <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
        </div>

        {/* Folder Body */}
        <div className="p-3 flex flex-col gap-2">
          {/* Folder Name */}
          <h3 className="text-sm font-semibold text-navy-700 dark:text-white truncate group-hover:text-brand-500 transition-colors duration-200">
            {folder.name}
          </h3>

          {/* Type Badge and Item Count */}
          <div className="flex items-center justify-between gap-2 text-xs">
            <span className="px-2 py-0.5 rounded-full bg-gray-100 dark:bg-navy-700 text-gray-700 dark:text-gray-300 font-medium whitespace-nowrap">
              {folder.type}
            </span>
            <span className="text-gray-600 dark:text-gray-400 font-medium whitespace-nowrap">
              {itemCount}
            </span>
          </div>

          {/* Updated Date */}
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {new Date(folder.updatedAt).toLocaleDateString()}
          </p>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="bg-white dark:bg-navy-800 rounded-lg shadow-2xl p-6 max-w-sm w-full mx-4 border border-gray-100 dark:border-navy-700">
            {/* Modal Header */}
            <div className="mb-4">
              <h3 className="text-lg font-bold text-navy-700 dark:text-white">
                Delete Folder
              </h3>
            </div>

            {/* Modal Body */}
            <div className="mb-6">
              <p className="text-gray-600 dark:text-gray-400">
                Are you sure you want to delete the folder <span className="font-semibold text-navy-700 dark:text-white">"{folder.name}"</span>? This action cannot be undone.
              </p>
            </div>

            {/* Modal Actions */}
            <div className="flex gap-3 justify-end">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleCancelDelete();
                }}
                disabled={isDeleting}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 dark:border-gray-600 dark:text-gray-300 font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleConfirmDelete();
                }}
                disabled={isDeleting}
                className="px-4 py-2 rounded-lg bg-red-600 text-white font-medium hover:bg-red-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isDeleting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <MdDelete className="text-base" />
                    Delete
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PremiumFolderCard;
